import React, { useState, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import Table from 'components/common/Table';
import ManifestShipmentExpandedRow from 'components/add_order/manifest/ManifestDetail/ManifestShipment/ManifestShipmentExpandedRow';
import ManifestShipmentSummaryPanel from 'components/add_order/manifest/ManifestDetail/ManifestShipment/ManifestShipmentSummaryPanel';

import { manifestApi } from 'api';
import { MANIFEST_DETAIL_SHIPMENT_COLUMNS } from 'constants/manifest';

const ManifestShipment = ({ manifestId, onManifestUpdate }) => {
  const tableRef = useRef(null);
  const [totalShipments, setTotalShipments] = useState(0);

  const refreshManifestData = useCallback(() => {
    tableRef.current?.refetch();
    onManifestUpdate?.();
  }, [onManifestUpdate]);

  const handleDataUpdate = useCallback((data, totalItems) => {
    setTotalShipments(totalItems);
  }, []);

  const handleRemoveShipment = async (manifestId, shipmentId) => {
    try {
      const response = await manifestApi.removeShipmentFromManifest(
        manifestId,
        shipmentId,
      );

      toast.success('Removed shipment from manifest');
      refreshManifestData();
    } catch (error) {
      toast.error('Failed to remove shipment from manifest');
    }
  };

  const extendedColumns = useMemo(() => {
    const removeColumn = {
      accessorKey: 'none',
      header: '',
      enableSorting: false,
      meta: {
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const shipmentId = original.id;

        return (
          <FontAwesomeIcon
            className="cursor-pointer"
            icon="trash-alt"
            onClick={() => handleRemoveShipment(manifestId, shipmentId)}
          />
        );
      },
    };

    return [...MANIFEST_DETAIL_SHIPMENT_COLUMNS, removeColumn];
  }, []);

  return (
    <div className="position-relative">
      <Table
        ref={tableRef}
        title={`Manifests #${manifestId}`}
        apiService={manifestApi}
        columns={extendedColumns}
        fetchConfig={{
          method: 'getShipmentsOfManifest',
          transformParams: params => {
            const transformedParams = new URLSearchParams();
            if (params.pageIndex !== undefined) {
              transformedParams.append('page', params.pageIndex + 1);
            }
            if (params.pageSize) {
              transformedParams.append('page_size', params.pageSize);
            }

            return transformedParams;
          },
        }}
        resourceId={manifestId}
        initialPageSize={25}
        renderExpandedRow={row => (
          <ManifestShipmentExpandedRow shipment={row.original} />
        )}
        onDataChange={handleDataUpdate}
      />

      <ManifestShipmentSummaryPanel
        manifestId={manifestId}
        onManifestUpdate={refreshManifestData}
        shipmentCount={totalShipments}
      />
    </div>
  );
};

export default ManifestShipment;
